import { useMemo } from 'react';
import dayjs from 'dayjs';
import localeData from 'dayjs/plugin/localeData';
import localizedFormat from 'dayjs/plugin/localizedFormat';

import { decode } from './format/decodeFormat';
import { Format } from './format';
import { useTranslation } from './useTranslation';

dayjs.extend(localeData);
dayjs.extend(localizedFormat);

/**
 * These hooks enable the date picker - they are in util-intl to encapsulate the date formatting logic here
 */

/**
 * Fetch the internationalised full names of months
 */
export function useMonthNames() {
  const { i18n } = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- result of dayjs.months depends on current locale
  return useMemo(() => dayjs.months(), [i18n.language]);
}

/**
 * Fetch the internationalised very short day abbreviations (2 character for English)
 */
export function useDaysMin() {
  const { i18n } = useTranslation();
  // eslint-disable-next-line react-hooks/exhaustive-deps -- result of dayjs.weekdaysMin depends on current locale
  return useMemo(() => dayjs.weekdaysMin(), [i18n.language]);
}

/**
 * Fetch the placeholder which demonstrates the format (e.g. DD/MM/YYYY, MM/DD/YYYY)
 * It is translatable (e.g. YYYY would be JJJJ in German)
 */
export function useShortDatePlaceholder() {
  const { t } = useTranslation();
  // This translation uses a different namespace to the default - because we're not statically translatable
  return useMemo(() => t(dayjs.localeData().longDateFormat(decode(Format.DATE_SHORT)), { ns: 'dateFormat' }), [t]);
}
